import type { Viewport } from './types';

/**
 * Math pure pour le composant MiniMap (Lot 2.J).
 *
 * Isolée du composant Svelte pour être testable sans DOM. Le composant se charge
 * uniquement du Canvas 2D et des events ; toute la géométrie vit ici.
 */

/**
 * Dimensions du thumbnail pour un pattern donné.
 *
 * On contraint le plus grand côté du pattern à `maxSize` pixels. `cellPx` est
 * le facteur cellule → pixel (peut être < 1 pour une grille 300×300 dans un
 * thumb 160×160).
 */
export function computeThumbDimensions(
  patternWidth: number,
  patternHeight: number,
  maxSize: number,
): { thumbW: number; thumbH: number; cellPx: number } {
  if (patternWidth <= 0 || patternHeight <= 0) {
    return { thumbW: 0, thumbH: 0, cellPx: 0 };
  }
  const cellPx = maxSize / Math.max(patternWidth, patternHeight);
  return {
    thumbW: Math.round(patternWidth * cellPx),
    thumbH: Math.round(patternHeight * cellPx),
    cellPx,
  };
}

/**
 * Rectangle (en coord thumbnail) qui matérialise la zone du pattern visible
 * dans le viewport principal. Coordonnées et tailles peuvent dépasser
 * [0, thumb*] : c'est au caller de clipper si nécessaire (un viewport zoomé
 * hors-grille donne un rect qui sort).
 */
export function computeViewportRect(
  viewport: Viewport,
  containerSize: { w: number; h: number },
  cellSize: number,
  cellPx: number,
): { x: number; y: number; w: number; h: number } {
  // Coord monde du coin haut-gauche écran : world = screen/scale - offset
  // À screen = (0, 0) : world = -offset
  const minWorldX = -viewport.offsetX;
  const minWorldY = -viewport.offsetY;
  const visibleWorldW = containerSize.w / viewport.scale;
  const visibleWorldH = containerSize.h / viewport.scale;

  // Coord cellule : world / cellSize, puis cellule → thumb : * cellPx
  return {
    x: (minWorldX / cellSize) * cellPx,
    y: (minWorldY / cellSize) * cellPx,
    w: (visibleWorldW / cellSize) * cellPx,
    h: (visibleWorldH / cellSize) * cellPx,
  };
}

/**
 * Vrai si l'intégralité du pattern est déjà visible dans le viewport courant
 * (rien à scroller). Sert à masquer la mini-map quand elle n'apporte rien.
 */
export function isPatternFullyVisible(
  viewport: Viewport,
  containerSize: { w: number; h: number },
  cellSize: number,
  patternWidth: number,
  patternHeight: number,
): boolean {
  const minWorldX = -viewport.offsetX;
  const minWorldY = -viewport.offsetY;
  const maxWorldX = minWorldX + containerSize.w / viewport.scale;
  const maxWorldY = minWorldY + containerSize.h / viewport.scale;
  return (
    minWorldX <= 0 &&
    minWorldY <= 0 &&
    maxWorldX >= patternWidth * cellSize &&
    maxWorldY >= patternHeight * cellSize
  );
}

/**
 * Recentre le viewport pour qu'une coord cellule du pattern tombe au centre
 * de la zone visible — sans toucher au scale.
 *
 * Utilisé par la mini-map quand l'utilisateur clique : (col, row) clic → centre.
 */
export function centerViewportOn(
  viewport: Viewport,
  col: number,
  row: number,
  containerSize: { w: number; h: number },
  cellSize: number,
): Viewport {
  // On veut : centre écran (w/2, h/2) corresponde à (col*cellSize, row*cellSize) monde
  // screen = (world + offset) * scale  ⇒  offset = screen/scale - world
  const worldX = col * cellSize;
  const worldY = row * cellSize;
  return {
    scale: viewport.scale,
    offsetX: containerSize.w / (2 * viewport.scale) - worldX,
    offsetY: containerSize.h / (2 * viewport.scale) - worldY,
  };
}

/**
 * Convertit un clic en coord thumbnail vers une coord cellule du pattern.
 * Clampe au pattern (un clic dans les marges du thumb tombe sur le bord).
 */
export function thumbToCell(
  thumbX: number,
  thumbY: number,
  cellPx: number,
  patternWidth: number,
  patternHeight: number,
): { col: number; row: number } {
  if (cellPx <= 0) return { col: 0, row: 0 };
  const col = Math.max(0, Math.min(patternWidth - 1, thumbX / cellPx));
  const row = Math.max(0, Math.min(patternHeight - 1, thumbY / cellPx));
  return { col, row };
}
